// Mobile-money helper: maps country → currency → payment link column.
// Returns null if no link is configured for the user's country.
import type { CurrencyCode } from "@/hooks/useCurrency";

export type MobileMoneyCountry = "NG" | "SN" | "CI" | "BF" | "ML" | "KE" | "BR";

export const MOBILE_MONEY_COUNTRIES: readonly MobileMoneyCountry[] = ["NG", "SN", "CI", "BF", "ML", "KE", "BR"];

const COUNTRY_TO_CURRENCY: Record<MobileMoneyCountry, CurrencyCode> = {
  NG: "NGN",
  SN: "XOF",
  CI: "XOF",
  BF: "XOF",
  ML: "XOF",
  KE: "KES",
  BR: "BRL",
};

const CURRENCY_TO_COLUMN: Record<"NGN" | "XOF" | "KES" | "BRL", string> = {
  NGN: "payment_link_mobile_ngn",
  XOF: "payment_link_mobile_xof",
  KES: "payment_link_mobile_kes",
  BRL: "payment_link_mobile_brl",
};

interface ProductRow {
  payment_link_mobile_ngn?: string | null;
  payment_link_mobile_xof?: string | null;
  payment_link_mobile_kes?: string | null;
  payment_link_mobile_brl?: string | null;
}

/**
 * Resolve the appropriate mobile-money payment link for a given product + country.
 * Returns `{ link, currency, country }` if eligible & configured, else `null`.
 *
 * Country detection is best-effort:
 *   1. explicit `country` arg
 *   2. localStorage `bp.country` (set by checkout form, etc.)
 *   3. locale heuristic (fr → SN, pt → BR)
 */
export function resolveMobileMoney(
  product: ProductRow | null | undefined,
  opts?: { country?: string; locale?: string },
): { link: string; currency: CurrencyCode; country: MobileMoneyCountry } | null {
  if (!product) return null;

  let country = (opts?.country ?? "").toUpperCase();
  if (!country && typeof window !== "undefined") {
    // Priority: manual (checkout form) > geo-detected (sessionStorage) > legacy bp.country
    country = (
      window.localStorage.getItem("bp.country.manual") ??
      window.sessionStorage.getItem("bp.country") ??
      window.localStorage.getItem("bp.country") ??
      ""
    ).toUpperCase();
  }
  if (!country && opts?.locale) {
    const l = opts.locale.slice(0, 2).toLowerCase();
    if (l === "pt") country = "BR";
    else if (l === "fr") country = "SN";
  }

  if (!MOBILE_MONEY_COUNTRIES.includes(country as MobileMoneyCountry)) return null;

  const currency = COUNTRY_TO_CURRENCY[country as MobileMoneyCountry];
  const col = CURRENCY_TO_COLUMN[currency as "NGN" | "XOF" | "KES" | "BRL"];
  const link = (product as Record<string, unknown>)[col] as string | null | undefined;
  if (!link) return null;

  return { link, currency, country: country as MobileMoneyCountry };
}
